import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';

import { ensureQualityEnabled } from '@/features/quality/guard.js';
import { isSupportedSourceRef } from '@/features/quality/LaunchSettingsSelectors.js';
import { LocalSuiteDetail } from '@/features/quality/LocalSuiteDetail.js';
import { QualitySuiteDetail } from '@/features/quality/QualitySuiteDetail.js';
import {
  asTestSource,
  type SourceRef,
  type TestSource,
} from '@/lib/api/quality-types.js';

// projectId rides the URL so a suite is fetched under its project's account override; it is optional
// (a cold deep-link without it falls back to the global env account). source picks the provider (a
// TestRail run, a Zephyr cycle, an Azure Test Plan suite, or an Xray Test Plan/Test Set) — the suite id
// alone is ambiguous across sources, so the Tests browser carries the clicked row's source here. It is
// narrowed by asTestSource against the one TEST_SOURCES list: a local copy of the members here went
// stale when Xray was added, so source=xray was dropped and the service fell back to its TestRail
// default, which loads no cases for an Xray container. sourceRef carries the canonical source link
// and coordinates, which disambiguate duplicate provider/container ids. caseId, when present,
// deep-links to a specific case in the suite (e.g. from a run's case row) — the viewer opens it
// selected and keeps the URL in sync as the user switches cases.

/** Parse and validate the canonical source identity carried in a suite deep link. */
export function parseSourceRefSearch(raw: unknown): SourceRef | undefined {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw) as unknown;
    } catch {
      return undefined;
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  try {
    const sourceRef = value as SourceRef;
    if (
      typeof sourceRef.provider !== 'string' ||
      typeof sourceRef.projectId !== 'string' ||
      (sourceRef.provider !== 'own-tms' &&
        typeof sourceRef.sourceLinkId !== 'string')
    ) {
      return undefined;
    }
    return isSupportedSourceRef(sourceRef) ? sourceRef : undefined;
  } catch {
    return undefined;
  }
}

export const Route = createFileRoute('/quality_/suites/$id')({
  component: QualitySuitePage,
  beforeLoad: () => ensureQualityEnabled(),
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    projectId?: string;
    caseId?: string;
    source?: TestSource;
    sourceRef?: SourceRef;
  } => {
    // Normalize empty/whitespace-only to undefined so the query key and the request agree (an empty
    // string is a falsy projectId for the fetch but would otherwise pollute the cache key).
    const projectId =
      typeof search.projectId === 'string' ? search.projectId.trim() : '';
    const caseId =
      typeof search.caseId === 'string' ? search.caseId.trim() : '';
    // Only accept a known source (trimmed, so "zephyr " matches); anything unknown falls back to the
    // server default (testrail).
    const sourceRef = parseSourceRefSearch(search.sourceRef);
    const source = sourceRef?.provider ?? asTestSource(search.source);
    return {
      projectId: projectId || undefined,
      caseId: caseId || undefined,
      source,
      ...(sourceRef ? { sourceRef } : {}),
    };
  },
});

function QualitySuitePage(): JSX.Element {
  const { id } = useParams({ from: '/quality_/suites/$id' });
  const { projectId, caseId, source, sourceRef } = useSearch({
    from: '/quality_/suites/$id',
  });
  if (source === 'own-tms') {
    return (
      <LocalSuiteDetail
        suiteId={id}
        projectId={projectId ?? sourceRef?.projectId}
        caseKey={caseId}
      />
    );
  }
  return (
    <QualitySuiteDetail
      suiteId={id}
      projectId={projectId}
      caseId={caseId}
      source={source}
      sourceRef={sourceRef}
    />
  );
}
